import React, { useMemo } from "react";
import { TreemapChart } from "../../../../../components/Charts";
import { useYearFilter } from "../../hooks/useYearFilter";
import { colorForCategory } from "../../../../../utils";
import { TransactionType } from "../../../../../utils/types";
import { BREAKPOINTS } from "../../../../../utils/constants";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { useGetGroupedTransactionsQuery } from "../../../../../services/transactions";
import type { GetGroupedTransactionsProps } from "../../../../Transactions/types";
import {
  SectionCard,
  SectionHeader,
  TitleBlock,
  SectionTitle,
  SubtitleText,
  ChartEmptyState,
  ChartErrorState,
} from "../../styles";
import { YearFilter } from "../YearFilter";
import Button, { ButtonTypes } from "../../../../../components/Button";
import type { CategoryBreakdownSectionProps, CategoryPoint } from "./types";

const TOP_N = 5;

export const CategoryBreakdownSection: React.FC<
  CategoryBreakdownSectionProps
> = ({ accountId, availableYears }) => {
  const yearFilter = useYearFilter(availableYears);
  const { selectedYear } = yearFilter;
  const isMobile = useResponsive(BREAKPOINTS.tabletMin);
  const treemapHeight = isMobile ? 250 : 300;

  const params = useMemo<GetGroupedTransactionsProps>(
    () => ({
      groupBy: "category",
      type: [TransactionType.EXPENSE],
      accountId,
      searchBy: "",
      limit: TOP_N,
      ...(selectedYear === null ? {} : { specificYear: selectedYear }),
    }),
    [accountId, selectedYear],
  );

  const {
    data = [],
    isLoading,
    isError: hasError,
    refetch,
  } = useGetGroupedTransactionsQuery(params);

  const categories = useMemo<CategoryPoint[]>(
    () => data.map((c) => ({ name: c.name, amount: c.totalAmount })),
    [data],
  );

  const handleRetry = () => {
    void refetch();
  };

  const treemapData = useMemo(
    () =>
      categories.map((c) => ({
        label: c.name,
        value: c.amount,
        color: colorForCategory(c.name),
      })),
    [categories],
  );

  const subtitle = selectedYear === null ? "All time" : String(selectedYear);

  const renderChartBody = () => {
    if (hasError) {
      return (
        <ChartErrorState role="alert">
          Couldn&apos;t load expense categories.
          <Button type={ButtonTypes.cancel} size="small" onClick={handleRetry}>
            Try again
          </Button>
        </ChartErrorState>
      );
    }

    if (!isLoading && categories.length === 0) {
      return (
        <ChartEmptyState>No expense categories for this period</ChartEmptyState>
      );
    }

    return (
      <TreemapChart
        data={treemapData}
        height={treemapHeight}
        emptyStateMessage="No category data available"
      />
    );
  };

  return (
    <SectionCard>
      <SectionHeader>
        <TitleBlock>
          <SectionTitle>Top expense categories</SectionTitle>
          <SubtitleText>{subtitle}</SubtitleText>
        </TitleBlock>
        <YearFilter {...yearFilter} />
      </SectionHeader>

      {renderChartBody()}
    </SectionCard>
  );
};
